import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PAGE_SIZE_OPTIONS } from '@wit-support/ui';
import { isYmd } from './period.js';
export function normalizeSearch(raw) {
    const out = {};
    if (raw === null || typeof raw !== 'object')
        return out;
    for (const [k, v] of Object.entries(raw)) {
        if (typeof v === 'string' && v.trim() !== '')
            out[k] = v;
        else if (typeof v === 'number' && Number.isFinite(v))
            out[k] = String(v);
        else if (typeof v === 'boolean')
            out[k] = String(v);
    }
    return out;
}
export function applySearchPatch(prev, patch) {
    const next = prev !== null && typeof prev === 'object' ? { ...prev } : {};
    for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null || v === '' || v === false)
            delete next[k];
        else
            next[k] = v;
    }
    return next;
}
export function useUrlSearch() {
    const raw = useSearch({ strict: false });
    const navigate = useNavigate();
    const update = useCallback((patch, opts) => {
        const page = patch['page'];
        void navigate({
            to: '.',
            search: (prev) => applySearchPatch(prev, patch),
            resetScroll: page !== undefined && page !== null && page !== '' && page !== false,
            ...(opts?.replace ? { replace: true } : {}),
        });
    }, [navigate]);
    const values = useMemo(() => normalizeSearch(raw), [raw]);
    return [values, update];
}
// --- кабинеты ---------------------------------------------------------------
export const ACCOUNTS_MEMORY_KEY = 'payback.accounts.v1';
/** В адресе: ни одного кабинета не выбрано. */
export const NO_ACCOUNTS = 'none';
const ACCOUNT_ID = /^[a-f0-9]{24}$/;
export function parseAccounts(v) {
    if (v === undefined)
        return null;
    if (v === NO_ACCOUNTS)
        return [];
    return [
        ...new Set(v
            .split(',')
            .map((s) => s.trim())
            .filter((s) => ACCOUNT_ID.test(s))),
    ];
}
export function readAccountsMemory() {
    try {
        const raw = globalThis.localStorage?.getItem(ACCOUNTS_MEMORY_KEY);
        if (!raw)
            return [];
        const v = JSON.parse(raw);
        return Array.isArray(v)
            ? v.filter((x) => typeof x === 'string' && ACCOUNT_ID.test(x))
            : [];
    }
    catch {
        return [];
    }
}
export function writeAccountsMemory(ids) {
    try {
        globalThis.localStorage?.setItem(ACCOUNTS_MEMORY_KEY, JSON.stringify(ids));
    }
    catch {
        // Память — удобство; без localStorage просто «все кабинеты».
    }
}
/**
 * Выбор кабинетов: адрес → память (только существующие кабинеты) → все.
 * `known` — id из `/api/ads/accounts` (пока не загружены — `null`).
 */
export function resolveAccounts(urlValue, known, memory) {
    const fromUrl = parseAccounts(urlValue);
    if (fromUrl !== null) {
        if (fromUrl.length === 0)
            return { ids: [], all: false, none: true };
        const ids = known ? fromUrl.filter((id) => known.includes(id)) : fromUrl;
        if (known && ids.length === known.length)
            return { ids: [], all: true, none: false };
        if (ids.length === 0)
            return { ids: [], all: true, none: false };
        return { ids, all: false, none: false };
    }
    if (known && memory.length) {
        const ids = memory.filter((id) => known.includes(id));
        if (ids.length > 0 && ids.length < known.length)
            return { ids, all: false, none: false };
    }
    return { ids: [], all: true, none: false };
}
// --- момент, модель, период -------------------------------------------------
const MOMENTS = ['t0', 't24', 'd14'];
export function readMoment(v) {
    return MOMENTS.includes(v) ? v : 'best';
}
export function readModel(v) {
    const s = v?.trim();
    return s && s.length <= 64 ? s : undefined;
}
export function readPeriod(s, fallback) {
    const from = s['from'];
    const to = s['to'];
    if (isYmd(from) && isYmd(to) && from <= to)
        return { from, to, custom: true };
    return { ...fallback, custom: false };
}
// --- разделение по версиям настроек воронки (S3) ------------------------------
/** `split=revision` в адресе: кампании с несколькими версиями настроек делятся на строки. */
export function readSplit(s) {
    return s['split'] === 'revision';
}
/**
 * Номер версии — ОТДЕЛЬНЫЙ параметр `revision=` (не часть ключа кампании: `#` в адресе — фрагмент).
 * Целое ≥ 1; мусор — `null`; без `split=revision` игнорируется.
 */
export function readRevision(s) {
    if (!readSplit(s))
        return null;
    const v = s['revision'];
    return v !== undefined && /^[1-9]\d{0,8}$/.test(v) ? Number(v) : null;
}
/**
 * Общее состояние раздела. `defaultPeriod` — период страницы, когда в адресе
 * его нет (переход из сайдбара); `knownAccounts` — id кабинетов из справочника.
 */
export function usePaybackUrl(defaultPeriod, knownAccounts) {
    const [search, set] = useUrlSearch();
    const period = readPeriod(search, defaultPeriod);
    const memory = useMemo(() => readAccountsMemory(), []);
    const accounts = useMemo(() => resolveAccounts(search['accounts'], knownAccounts, memory), [search, knownAccounts, memory]);
    const setPeriod = useCallback((p) => set({ from: p.from, to: p.to, page: undefined }), [set]);
    const setAccounts = useCallback((ids, allIds) => {
        const uniq = [...new Set(ids)];
        const all = uniq.length === allIds.length && allIds.every((id) => uniq.includes(id));
        writeAccountsMemory(all || uniq.length === 0 ? [] : uniq);
        set({
            accounts: all ? undefined : uniq.length === 0 ? NO_ACCOUNTS : uniq.join(','),
            page: undefined,
        });
    }, [set]);
    return {
        from: period.from,
        to: period.to,
        customPeriod: period.custom,
        accounts,
        moment: readMoment(search['moment']),
        model: readModel(search['model']),
        about: search['about'] === '1' || search['about'] === 'true',
        split: readSplit(search),
        revision: readRevision(search),
        search,
        set,
        setPeriod,
        setAccounts,
    };
}
/**
 * Сортировка, страница, размер и фильтр-чип таблицы в адресе. `prefix` —
 * для нескольких таблиц на одной странице (`acc` → `accsort`, `accpage`…).
 */
export function useTableUrl(prefix, keys, defaultSort, defaultSize = 25) {
    const [search, set] = useUrlSearch();
    const k = (name) => `${prefix}${name}`;
    const sortKey = search[k('sort')];
    const dir = search[k('dir')];
    const sort = keys.includes(sortKey)
        ? {
            key: sortKey,
            dir: dir === 'asc' || dir === 'desc' ? dir : defaultSort.dir,
        }
        : defaultSort;
    const pageN = Number(search[k('page')]);
    const sizeN = Number(search[k('size')]);
    const size = PAGE_SIZE_OPTIONS.includes(sizeN) ? sizeN : defaultSize;
    return {
        sort,
        setSort: (s) => set({
            [k('sort')]: s.key === defaultSort.key && s.dir === defaultSort.dir ? undefined : s.key,
            [k('dir')]: s.key === defaultSort.key && s.dir === defaultSort.dir ? undefined : s.dir,
            [k('page')]: undefined,
        }),
        page: Number.isInteger(pageN) && pageN >= 1 ? pageN : 1,
        setPage: (n) => set({ [k('page')]: n > 1 ? n : undefined }),
        size,
        setSize: (n) => set({ [k('size')]: n === defaultSize ? undefined : n, [k('page')]: undefined }),
        verdict: search[k('verdict')],
        setVerdict: (v) => set({ [k('verdict')]: v, [k('page')]: undefined }),
    };
}
export const PAYBACK_PATHS = {
    summary: '/payback',
    campaigns: '/payback/campaigns',
    portrait: '/payback/portrait',
    trend: '/payback/trend',
};
/**
 * Ссылка на страницу раздела: переносит `accounts`, `moment`, `model`
 * текущего адреса, период и прочее — из `patch` (без периода — период
 * страницы по умолчанию).
 */
export function linkTo(page, patch, current) {
    const carry = {
        accounts: current['accounts'],
        moment: current['moment'],
        model: current['model'],
    };
    return { to: PAYBACK_PATHS[page], search: applySearchPatch({}, { ...carry, ...patch }) };
}
//# sourceMappingURL=url-state.js.map